import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Loader2, MessageCircle, Pencil, Plus, Power, QrCode, ShieldAlert, Trash2,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  aprovarTemplateLocal, criarTemplate, desconectarCanal, excluirCanal, getCanal,
  getCanalEstado, getMensageriaConfig, getQrCanal, listTemplates,
  salvarMensageriaConfig,
  type CanalConexao, type CanalEstado, type MensageriaConfig, type ProvedorCanal,
  type WhatsTemplate,
} from '@/services/atendimento'
import EditarConexaoModal from '@/components/atendimento/EditarConexaoModal'
import QrPairingModal from '@/components/atendimento/QrPairingModal'

const PROVEDOR_INFO: Record<ProvedorCanal, { label: string; cor: string }> = {
  simulado: { label: 'Simulado', cor: '#8494A6' },
  cloud_api: { label: 'Cloud API (oficial)', cor: '#2E7D32' },
  baileys: { label: 'WhatsApp Web (QR)', cor: '#F57C00' },
}

export default function ConfigWhatsAppPage() {
  const { current, papel } = useAuth()
  const souTitular = papel === 'medico_titular'

  const [canal, setCanal] = useState<CanalConexao | null>(null)
  const [cfg, setCfg] = useState<MensageriaConfig>({ whatsapp_ativo: true, envio_automatico: false, teto_envios_hora: 60 })
  const [templates, setTemplates] = useState<WhatsTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const [estado, setEstado] = useState<CanalEstado | null>(null)
  const [qr, setQr] = useState<string | null>(null)

  const [editAberto, setEditAberto] = useState(false)
  const [qrAberto, setQrAberto] = useState(false)

  const [tplNome, setTplNome] = useState('')
  const [tplCorpo, setTplCorpo] = useState('')

  const carregar = useCallback(async () => {
    if (!current) return
    setErro(null)
    try {
      const [c, m, t] = await Promise.all([
        getCanal(current.workspace.id),
        getMensageriaConfig(current.workspace.id),
        listTemplates(current.workspace.id),
      ])
      setCanal(c)
      setCfg(m)
      setTemplates(t)
    } catch {
      setErro('Não foi possível carregar. A migração phase8 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  // Conexão por QR: o worker na VM publica QR, status e heartbeat no banco.
  // O QR do WhatsApp Web expira a cada ~20s e é reemitido — 5s de poll basta.
  useEffect(() => {
    if (!canal || canal.provedor !== 'baileys') { setEstado(null); setQr(null); return }
    let vivo = true
    const tick = async () => {
      try {
        const [e, q] = await Promise.all([getCanalEstado(canal.id), getQrCanal(canal.id)])
        if (!vivo) return
        setEstado(e)
        setQr(q.imagem)
      } catch { /* rede instável: mantém o último estado conhecido */ }
    }
    void tick()
    const id = setInterval(tick, 5000)
    return () => { vivo = false; clearInterval(id) }
  }, [canal])

  // Conectou enquanto o QR estava aberto: fecha sozinho.
  useEffect(() => {
    if (qrAberto && estado?.status === 'conectado') setQrAberto(false)
  }, [qrAberto, estado?.status])

  async function desconectar() {
    if (!current || !canal) return
    if (!window.confirm('Desconectar o WhatsApp? A sessão atual é apagada e será preciso escanear um novo QR.')) return
    setBusy(true); setErro(null); setAviso(null)
    try {
      await desconectarCanal(current.workspace.id, canal.id)
      setQr(null)
      setAviso('Sessão encerrada. Um novo QR aparece aqui em alguns segundos.')
      await carregar()
    } catch {
      setErro('Não foi possível desconectar.')
    } finally { setBusy(false) }
  }

  async function excluir() {
    if (!current || !canal) return
    if (!window.confirm(`Excluir a conexão "${canal.nome}"? Isso não apaga o histórico de conversas já registrado.`)) return
    setBusy(true); setErro(null); setAviso(null)
    try {
      await excluirCanal(current.workspace.id, canal.id)
      await carregar()
    } catch {
      setErro('Não foi possível excluir — pode haver conversas vinculadas a esta conexão.')
    } finally { setBusy(false) }
  }

  async function salvarConfig(patch: Partial<MensageriaConfig>) {
    if (!current) return
    const novo = { ...cfg, ...patch }
    setCfg(novo)
    try { await salvarMensageriaConfig(current.workspace.id, novo) } catch { setErro('Falha ao salvar a configuração.') }
  }

  async function novoTemplate(e: React.FormEvent) {
    e.preventDefault()
    if (!current || tplNome.trim().length < 3 || tplCorpo.trim().length < 5) return
    setBusy(true)
    try {
      await criarTemplate({ workspaceId: current.workspace.id, canalId: canal?.id ?? null, nome: tplNome, corpo: tplCorpo })
      setTplNome(''); setTplCorpo('')
      await carregar()
    } catch {
      setErro('Não foi possível criar o template.')
    } finally { setBusy(false) }
  }

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
      </div>
    )
  }

  const statusAtual = canal ? (estado?.status ?? canal.status) : null
  const conectadoBaileys = canal?.provedor === 'baileys' && statusAtual === 'conectado'

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 className="page-title">Conexões</h1>
        <p className="page-subtitle" style={{ margin: 0 }}>Canal de WhatsApp vinculado ao Atendimento.</p>
      </header>

      {!souTitular && (
        <div className="badge badge-warn" style={{ marginBottom: 12, display: 'block', padding: '8px 12px' }}>
          Só o médico titular altera a conexão. Você pode consultar.
        </div>
      )}

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}
      {aviso && <div className="badge badge-ok" role="status" style={{ padding: '8px 12px', marginBottom: 12, display: 'block' }}>{aviso}</div>}

      {/* chave-mestra */}
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <ShieldAlert size={18} aria-hidden style={{ color: cfg.whatsapp_ativo ? 'var(--ok)' : 'var(--danger)' }} />
          <strong style={{ fontSize: 15 }}>Chave-mestra de envio</strong>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
          Desligar aqui bloqueia <strong>todo</strong> envio de WhatsApp deste workspace — inclusive automações.
          O bloqueio vale no banco, não só na tela.
        </p>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
          <input type="checkbox" checked={cfg.whatsapp_ativo} disabled={!souTitular}
            onChange={e => salvarConfig({ whatsapp_ativo: e.target.checked })} style={{ width: 18, height: 18 }} />
          Envio de WhatsApp ativo
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
          <input type="checkbox" checked={cfg.envio_automatico} disabled={!souTitular}
            onChange={e => salvarConfig({ envio_automatico: e.target.checked })} style={{ width: 18, height: 18 }} />
          Permitir disparos automáticos (lembretes/régua) — desligado por padrão
        </label>
        <label style={{ fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          Teto de envios automáticos por hora:
          <input
            type="number" min={1} max={500} className="field-input" style={{ width: 100, minHeight: 34 }}
            value={cfg.teto_envios_hora} disabled={!souTitular}
            onChange={e => salvarConfig({ teto_envios_hora: Number(e.target.value) })}
          />
        </label>
      </section>

      {/* resumo da conexão — mesma organização do painel de conexões do Grupo-Soumed */}
      <section className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          aria-hidden
          style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <MessageCircle size={20} color="#fff" aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15 }}>{canal?.nome || 'Nenhuma conexão'}</strong>
            {canal && (
              <span
                className="badge"
                style={{ background: `${PROVEDOR_INFO[canal.provedor].cor}1a`, color: PROVEDOR_INFO[canal.provedor].cor, fontSize: 11 }}
              >
                {PROVEDOR_INFO[canal.provedor].label}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {canal?.telefone_exibicao && <span>{canal.telefone_exibicao}</span>}
            {canal && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span
                  aria-hidden
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: statusAtual === 'conectado' ? 'var(--ok)' : statusAtual === 'erro' ? 'var(--danger)' : 'var(--muted)',
                  }}
                />
                {statusAtual === 'conectado' ? 'Conectado' : statusAtual === 'erro' ? 'Erro' : 'Desconectado'}
              </span>
            )}
          </div>
        </div>

        {souTitular && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {canal?.provedor === 'baileys' && (
              conectadoBaileys ? (
                <button
                  type="button" className="btn-secondary" aria-label="Desconectar" title="Desconectar"
                  style={{ minHeight: 34, width: 34, padding: 0, justifyContent: 'center', color: 'var(--danger)' }}
                  onClick={desconectar} disabled={busy}
                >
                  <Power size={15} aria-hidden />
                </button>
              ) : (
                <button
                  type="button" className="btn-secondary" aria-label="Parear por QR code" title="Parear por QR code"
                  style={{ minHeight: 34, width: 34, padding: 0, justifyContent: 'center' }}
                  onClick={() => setQrAberto(true)}
                >
                  <QrCode size={15} aria-hidden />
                </button>
              )
            )}
            <button
              type="button" className="btn-secondary" aria-label={canal ? 'Editar conexão' : 'Nova conexão'} title={canal ? 'Editar conexão' : 'Nova conexão'}
              style={{ minHeight: 34, width: 34, padding: 0, justifyContent: 'center' }}
              onClick={() => setEditAberto(true)}
            >
              {canal ? <Pencil size={15} aria-hidden /> : <Plus size={15} aria-hidden />}
            </button>
            {canal && (
              <button
                type="button" className="btn-secondary" aria-label="Excluir conexão" title="Excluir conexão"
                style={{ minHeight: 34, width: 34, padding: 0, justifyContent: 'center', color: 'var(--danger)' }}
                onClick={excluir} disabled={busy}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            )}
          </div>
        )}
      </section>

      {/* templates — só existem na conexão oficial */}
      {canal?.provedor !== 'baileys' && (
      <section className="card" style={{ padding: 18 }}>
        <strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Templates</strong>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          Fora da janela de 24h, o WhatsApp só entrega mensagem por template aprovado. Use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>… para as variáveis.
        </p>

        {templates.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {templates.map(t => (
              <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.nome}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t.corpo}</div>
                </div>
                <span className={`badge ${t.status === 'APPROVED' ? 'badge-ok' : t.status === 'REJECTED' ? 'badge-danger' : 'badge-warn'}`}>{t.status}</span>
                {souTitular && t.status !== 'APPROVED' && (
                  <button
                    className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                    onClick={async () => { if (!current) return; await aprovarTemplateLocal(current.workspace.id, t.id); await carregar() }}
                  >
                    {canal?.provedor === 'simulado' ? 'Aprovar (simulado)' : 'Marcar aprovado'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {souTitular && (
          <form onSubmit={novoTemplate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <input className="field-input" placeholder="nome_do_template" value={tplNome} onChange={e => setTplNome(e.target.value)} aria-label="Nome do template" />
            </div>
            <textarea
              className="field-input" rows={2} style={{ resize: 'vertical' }}
              placeholder="Olá {{1}}, sua consulta está marcada para {{2}}. Podemos confirmar?"
              value={tplCorpo} onChange={e => setTplCorpo(e.target.value)} aria-label="Corpo do template"
            />
            <div>
              <button type="submit" className="btn-primary" disabled={busy}>
                <Plus size={15} aria-hidden /> Criar template
              </button>
            </div>
          </form>
        )}
      </section>
      )}

      <div style={{ marginTop: 14 }}>
        <Link to="/atendimento" style={{ fontSize: 13.5, color: 'var(--primary-deep)', fontWeight: 600 }}>← Ir para o Atendimento</Link>
      </div>

      {editAberto && current && (
        <EditarConexaoModal
          workspaceId={current.workspace.id}
          canal={canal}
          onClose={() => setEditAberto(false)}
          onSaved={async a => { setAviso(a); await carregar() }}
        />
      )}

      {qrAberto && canal && (
        <QrPairingModal
          canal={canal}
          estado={estado}
          qr={qr}
          souTitular={souTitular}
          onClose={() => setQrAberto(false)}
        />
      )}
    </div>
  )
}
